import { Component } from '@theme/component';

/**
 * Share button for the Zauq share block. Opens the device share sheet where the Web Share API
 * exists, otherwise copies the page link and announces the result in the block's status region.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} status - Live region that announces copy results.
 *
 * @extends Component<Refs>
 */
class ZauqShare extends Component {
  requiredRefs = ['status'];

  /** @type {number | undefined} */
  #clearTimer;

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#clearTimer);
  }

  /**
   * Handles the share button click.
   * @param {MouseEvent} _event
   */
  async share(_event) {
    const { url, title } = this.dataset;
    if (!url) return;

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        // The shopper dismissed the share sheet.
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#announce(this.dataset.copiedMessage);
    } catch {
      this.#announce(this.dataset.failedMessage);
    }
  }

  /** @param {string | undefined} message */
  #announce(message) {
    if (!message) return;

    const { status } = this.refs;
    clearTimeout(this.#clearTimer);

    // Clear first so repeating the same message is announced again.
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });

    this.#clearTimer = window.setTimeout(() => {
      status.textContent = '';
    }, 4000);
  }
}

if (!customElements.get('zauq-share')) {
  customElements.define('zauq-share', ZauqShare);
}
